import { NavLink, useParams } from "react-router-dom";
import { Icon, type IconName } from "../icons";
import { usePersona, type Persona } from "../../hooks/usePersona";
import { useMe } from "../../hooks/useMe";
import { useAppStore } from "../../state/useAppStore";
import { roleLabel } from "../../lib/roleLabel";

interface NavSpec {
  key: string;
  label: string;
  icon: IconName;
  path: string;
  show: ReadonlySet<Persona>;
  section: "work" | "orchestration" | "admin";
}

const ALL: ReadonlySet<Persona> = new Set([
  "owner",
  "operator",
  "staff",
  "approver",
  "reviewer",
  "cxo",
  "agent_handler",
]);

/**
 * Who sees what.
 *
 * Every role used to see the same five destinations, which made four personas
 * one product with four names on it. What each role is for is in
 * `auth/personas.ts`, and this table follows it:
 *
 * - The **supervisor** owns the roster and the autonomy of each decision.
 * - A **team member** works their queue; the roster and the regulation
 *   lookup are context for it.
 * - The **compliance reviewer** decides obligations, so the lookup and the
 *   evidence behind a promotion are theirs; the stage controls are not.
 * - The **agent handler** maintains the agents themselves. Judgement forks
 *   are never theirs — they hold no business decision — and a queue that is
 *   always empty is a page that reads as broken. Agent health is theirs
 *   instead: what failed, and what a schedule could not do.
 */
const DECIDERS: ReadonlySet<Persona> = new Set([
  "cxo",
  "owner",
  "operator",
  "reviewer",
  "approver",
  "staff",
]);

const ROSTER: ReadonlySet<Persona> = new Set([
  "cxo",
  "owner",
  "operator",
  "reviewer",
  "staff",
  "agent_handler",
]);

/** The regulation lookup: for the people who answer to it. */
const LOOKUP: ReadonlySet<Persona> = new Set([
  "cxo",
  "owner",
  "operator",
  "reviewer",
  "staff",
]);

/** Telemetry is evidence about autonomy; moving a stage stays the CXO's. */
const TELEMETRY: ReadonlySet<Persona> = new Set([
  "cxo",
  "owner",
  "reviewer",
  "agent_handler",
]);

/** Versions of each agent, their evals and approval: the Supervisor's (a04). */
const STUDIO: ReadonlySet<Persona> = new Set(["cxo", "owner"]);

/** Anyone's thread, read-only: for the people who answer for it (§5.4). */
const THREADS: ReadonlySet<Persona> = new Set(["cxo", "owner", "reviewer"]);

/** Failures and schedules — the agent handler's job, and the owner's problem. */
const HEALTH: ReadonlySet<Persona> = new Set(["cxo", "owner", "agent_handler"]);

/**
 * The Efficiency Ledger. Hidden while it had nothing in it; shown now that
 * it counts rows, because "hours returned" is the claim the whole product
 * asks to be believed on and hiding it made the claim unfalsifiable.
 */
const LEDGER: ReadonlySet<Persona> = new Set([
  "cxo",
  "owner",
  "reviewer",
  "operator",
  "staff",
  "approver",
]);

// "Routines" (plays) and "Ledger" (outcomes) are not listed. Both routes
// still exist and still work; neither has anything in it in workforce mode —
// no plays are seeded, and the Efficiency Ledger is not built. A nav entry
// that leads to an empty page reads as a broken product rather than an
// unfinished one, so they come back when they have something to show.
const ITEMS: readonly NavSpec[] = [
  {
    // The landing route, and until now the only page with no way back to it:
    // signing in put you here and the first click stranded it.
    key: "dashboard",
    label: "Home",
    icon: "dashboard",
    path: "dashboard",
    show: ALL,
    section: "work",
  },
  {
    key: "operator",
    label: "Decisions",
    icon: "actions",
    path: "operator",
    show: DECIDERS,
    section: "work",
  },
  {
    key: "settings",
    label: "Settings",
    icon: "settings",
    path: "settings",
    show: ALL,
    section: "admin",
  },
];

export function LeftNav() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const persona = usePersona(workspaceId);
  const { data: me } = useMe();

  const visible = ITEMS.filter((item) =>
    persona ? item.show.has(persona) : item.show.has("operator"),
  );

  const workItems = visible.filter((i) => i.section === "work");
  const orchItems = visible.filter((i) => i.section === "orchestration");
  const adminItems = visible.filter((i) => i.section === "admin");
  const p = persona ?? "operator";
  const showRoster = ROSTER.has(p);
  const showTelemetry = TELEMETRY.has(p);
  const showHealth = HEALTH.has(p);
  const showLookup = LOOKUP.has(p);
  const showLedger = LEDGER.has(p);
  const showStudio = STUDIO.has(p);
  const showThreads = THREADS.has(p);
  const showWorkforce = showRoster || showTelemetry || showHealth || showLookup || showLedger;


  const navCollapsed = useAppStore((s) => s.navCollapsed);
  const toggleNav = useAppStore((s) => s.toggleNav);

  return (
    <aside className="nav" data-testid="leftnav">
      {workItems.map((item) => (
        <NavItem key={item.key} item={item} workspaceId={workspaceId ?? ""} />
      ))}

      {orchItems.length > 0 ? (
        <>
          <div className="nav-section">Orchestration</div>
          {orchItems.map((item) => (
            <NavItem key={item.key} item={item} workspaceId={workspaceId ?? ""} />
          ))}
        </>
      ) : null}

      {showWorkforce ? (
        <>
          <div className="nav-section">Workforce</div>
          {/* The roster, not the legacy prompt-hand catalogue. Both were
              labelled "Workforce", and the one a person would click went to
              the catalogue. */}
          {showRoster ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/workforce`}
              title="Workforce"
              aria-label="Workforce"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-hands-library"
            >
              <span className="ic"><Icon name="bot" /></span>
              <span>Workforce</span>
            </NavLink>
          ) : null}
          {showHealth ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/agent-health`}
              title="Runs and schedules"
              aria-label="Runs and schedules"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-agent-health"
            >
              <span className="ic"><Icon name="warn" /></span>
              <span>Runs and schedules</span>
            </NavLink>
          ) : null}
          {/* A lookup over GraphIQ — which law applied on a date, which rules
              apply — deliberately not a chat. */}
          {showLedger ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/ledger`}
              title="Efficiency Ledger"
              aria-label="Efficiency Ledger"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-ledger"
            >
              <span className="ic"><Icon name="governance" /></span>
              <span>Efficiency Ledger</span>
            </NavLink>
          ) : null}
          {showStudio ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/templates`}
              title="Template studio"
              aria-label="Template studio"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-templates"
            >
              <span className="ic"><Icon name="factory" /></span>
              <span>Template studio</span>
            </NavLink>
          ) : null}
          {showStudio ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/systems`}
              title="Systems"
              aria-label="Systems"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-systems"
            >
              <span className="ic"><Icon name="flow" /></span>
              <span>Systems</span>
            </NavLink>
          ) : null}
          {showThreads ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/threads`}
              title="Threads"
              aria-label="Threads"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-threads"
            >
              <span className="ic"><Icon name="msg" /></span>
              <span>Threads</span>
            </NavLink>
          ) : null}
          {/* What each decision has earned, and what it has not. */}
          {showTelemetry ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/autonomy`}
              title="Autonomy"
              aria-label="Autonomy"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-autonomy"
            >
              <span className="ic"><Icon name="target" /></span>
              <span>Autonomy</span>
            </NavLink>
          ) : null}
          {/* A lookup over GraphIQ — which law applied on a date, which
              rules apply — deliberately not a chat. For the people who answer
              to it; the agent handler does not. */}
          {showLookup ? (
            <NavLink
              to={`/workspaces/${workspaceId ?? ""}/regulatory`}
              title="Regulatory memory"
              aria-label="Regulatory memory"
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid="navitem-regulatory"
            >
              <span className="ic"><Icon name="star" /></span>
              <span>Regulatory memory</span>
            </NavLink>
          ) : null}
        </>
      ) : null}

      {adminItems.length > 0 ? (
        <>
          <div className="nav-section">Workspace</div>
          {adminItems.map((item) => (
            <NavItem key={item.key} item={item} workspaceId={workspaceId ?? ""} />
          ))}
        </>
      ) : null}

      <div className="nav-foot">
        <div className="avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
          {(me?.user.displayName ?? "?")
            .split(" ")
            .map((s) => s.charAt(0).toUpperCase())
            .join("")
            .slice(0, 2) || "?"}
        </div>
        <div className="who">
          <div>{me?.user.displayName ?? "—"}</div>
          <div className="role">
            <b>{roleLabel(persona)}</b>
          </div>
        </div>
        {/* The collapse toggle lives beside the person, at the bottom, where
            every sidebar a person already uses keeps it. */}
        <button
          type="button"
          className="nav-collapse-toggle"
          onClick={toggleNav}
          aria-label={navCollapsed ? "expand navigation" : "collapse navigation"}
          title={navCollapsed ? "Expand navigation" : "Collapse navigation"}
          data-testid="leftnav-collapse-toggle"
        >
          <span className="nav-hamburger" aria-hidden="true">
            {navCollapsed ? "»" : "«"}
          </span>
        </button>
      </div>
    </aside>
  );
}

function NavItem({ item, workspaceId }: { item: NavSpec; workspaceId: string }) {
  const to = `/workspaces/${workspaceId}/${item.path}`;
  return (
    <NavLink
      to={to}
      // The title is what a person sees when the nav is collapsed to its
      // icon strip; without it the icons are a guessing game.
      title={item.label}
      aria-label={item.label}
      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
      data-testid={`navitem-${item.key}`}
    >
      <span className="ic">
        <Icon name={item.icon} />
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}
